import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Typography,
  Box,
  Badge,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { getStyles } from "src/styles/theme";
import defaultStyles from "./styles";
import { useAppSelector } from "src/redux/store";

export type DashBoardTable = {
  topHeader: string[];
  data: any;
  onEdit?: (row: any) => void;
  onDelete?: (row: any) => void;
  status?: string;
  editable?: boolean;
  isPlantedSeedlingAvailable?: boolean;
  onChangeSeedlings?: (id: string, value: number) => void;
  onUpdateDetails?: (row: any) => void;
  onQueryClick?: (row: any) => void;
};

export function SeedlingTable({
  data,
  topHeader,
  onEdit,
  onDelete,
  status,
  editable = false,
  isPlantedSeedlingAvailable = true,
  onChangeSeedlings,
  onUpdateDetails,
  onQueryClick,
}: DashBoardTable) {
  const styles = getStyles(defaultStyles);
  const user = useAppSelector((state) => state.root.user);
  if (user?.data?.role !== "Implementing Agency") {
    editable = false;
  }
  const isDno =
    user?.data?.role === "Implementing Agency" && user?.data?.userType === null;
  const showUpdateDetails = editable && isDno && !!onUpdateDetails;
  const showDetailsColumn = showUpdateDetails || !!onQueryClick;
  const showActions =
    (status === "DRAFT" || status === "UNDER_QUERY") &&
    user?.data?.userType !== "INDIVIDUAL";
  const totalSeedlings = data.reduce(
    (sum, item) => sum + (item.numberOfSeedlings || 0),
    0
  );
  const totalPlantedSeedlings = data.reduce(
    (sum, item) => sum + (item.numberOfPlantedSeedlings || 0),
    0
  );
  return (
    <TableContainer component={Paper} sx={{ height: "fit-content" }}>
      <Table sx={{ minWidth: 600 }} aria-label="simple table">
        <TableHead {...styles("header")}>
          <TableRow>
            {topHeader.map((item, index) => (
              <TableCell align="center" {...styles("cell")} key={index}>
                {item}
              </TableCell>
            ))}
            {showDetailsColumn && (
              <TableCell align="center" {...styles("cell")}>
                {showUpdateDetails ? "Update Details" : "Query"}
              </TableCell>
            )}
            {showActions && (
              <TableCell align="center" {...styles("cell")}>
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((seedData, index) => {
            if (!seedData) return null;
            return (
              <TableRow key={index}>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "100px" })}
                >
                  {seedData.commonName}
                </TableCell>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "100px" })}
                >
                  {seedData.botanicalName}
                </TableCell>
                <TableCell
                  align="center"
                  {...styles("cell", { width: "120px" })}
                >
                  {editable && onChangeSeedlings ? (
                    <TextField
                      type="number"
                      size="small"
                      value={seedData.numberOfSeedlings ?? 0}
                      onChange={(event) => {
                        const value = event.target.value;
                        const numericValue = value === "" ? 0 : Number(value);
                        if (seedData.id) {
                          onChangeSeedlings(seedData.id, numericValue);
                        }
                      }}
                      inputProps={{
                        min: 0,
                        style: { textAlign: "center" },
                      }}
                      sx={{ width: "100%" }}
                    />
                  ) : (
                    seedData.numberOfSeedlings ?? 0
                  )}
                </TableCell>
                {isPlantedSeedlingAvailable && (
                  <TableCell
                    align="center"
                    {...styles("cell", { width: "120px" })}
                  >
                    {seedData.numberOfPlantedSeedlings ?? 0}
                  </TableCell>
                )}

                {showDetailsColumn && (
                  <TableCell
                    align="center"
                    {...styles("cell", { width: "140px" })}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      {showUpdateDetails &&
                        (() => {
                          const isQueryPending =
                            (seedData.pendingQueryCount || 0) > 0;
                          const isFirstTime =
                            !seedData?.fromDate &&
                            !seedData?.toDate &&
                            (!seedData?.numberOfPlantedSeedlings ||
                              Number(seedData?.numberOfPlantedSeedlings) === 0);

                          const canUpdate =
                            seedData.canUpdateDetails ??
                            (isQueryPending || isFirstTime);
                          if (isQueryPending && canUpdate) {
                            return (
                              <Typography
                                variant="body2"
                                color="error"
                                onClick={() => onUpdateDetails(seedData)}
                                style={{
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  fontSize: "15px",
                                  fontWeight: "bold",
                                }}
                              >
                                Update
                              </Typography>
                            );
                          }

                          if (isFirstTime && canUpdate) {
                            return (
                              <Typography
                                variant="body2"
                                color="primary"
                                onClick={() => onUpdateDetails(seedData)}
                                style={{
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  fontSize: "15px",
                                }}
                              >
                                Update
                              </Typography>
                            );
                          }

                          return (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.disabled",
                                fontSize: "15px",
                                cursor: "default",
                                userSelect: "none",
                              }}
                            >
                              Update
                            </Typography>
                          );
                        })()}
                      {onQueryClick && (
                        <Box
                          onClick={() => onQueryClick(seedData)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            padding: "2px",
                            borderRadius: "4px",
                            "&:hover": {
                              backgroundColor: "#e3f2fd",
                            },
                          }}
                        >
                          <Badge
                            badgeContent={seedData.pendingQueryCount || 0}
                            color="error"
                            max={99}
                            sx={{
                              "& .MuiBadge-badge": {
                                fontSize: "10px",
                                height: "16px",
                                minWidth: "16px",
                                padding: "0 3px",
                              },
                            }}
                          >
                            <ChatBubbleOutlineIcon
                              fontSize="small"
                              sx={{
                                color:
                                  seedData.pendingQueryCount > 0
                                    ? "#d32f2f"
                                    : "#2196f3",
                              }}
                            />
                          </Badge>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                )}

                {showActions && (
                  <TableCell
                    align="center"
                    {...styles("cell", { width: "100px" })}
                  >
                    <>
                      <IconButton
                        aria-label="edit"
                        onClick={() => onEdit(seedData)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        aria-label="delete"
                        onClick={() => onDelete(seedData.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          <TableRow>
            <TableCell></TableCell>
            <TableCell align="center">Total:</TableCell>
            <TableCell align="center">
              <strong>{totalSeedlings}</strong>
            </TableCell>
            {isPlantedSeedlingAvailable && (
              <TableCell align="center">
                <strong>{totalPlantedSeedlings}</strong>
              </TableCell>
            )}
            {showDetailsColumn && <TableCell />}
            {showActions && <TableCell />}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
